import I18n from 'I18n';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { EmptySimpleDate, makeMoment } from 'foundations-components/transitional/utils/SimpleDate';
import { Text } from 'foundations-components/components/Text';
import { DateInput } from 'foundations-components/transitional/components/DateInput';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { HR } from 'foundations-components/transitional/components/HR';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Form } from 'foundations-components/transitional/components/Form';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { AbstractProgress } from 'foundations-components/transitional/components/AbstractProgress';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useSubscription } from '../../../hooks/useSubscription';
import { SubscriptionStatus } from '../../../types/subscriptions';
import { EMPTY_VALUE, TEST_IDS, URLS } from '../../../utils/constants';
import { createSimpleDate } from '../../../utils/dateTime';
import { handleError } from '../../../utils/errorHandling';
import { useTracker } from '../../../utils/usageTracker';
import { useResumeSubscription } from './useResumeSubscription';
import { useResumeSubscriptionQuery } from './useResumeSubscriptionQuery';
import { useNotificationsQuery } from '../hooks/useNotificationsQuery';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledTile = styled.div.withConfig({
  displayName: "ResumeSubscriptionModal__StyledTile",
  componentId: "sc-8ekrcc-0"
})(({
  theme
}) => `
  border: 1px solid ${theme.color['border-core-subtle']};
  border-radius: 4px;
  padding: 8px 16px;
  margin: 0 0 12px;
  background-color: ${theme.color['fill-surface-recessed']};
`);
function getDescriptionMessage(collectionProcess, statusAfterUnpause) {
  if (statusAfterUnpause === SubscriptionStatus.Unpaid) {
    return /*#__PURE__*/_jsx(Alert, {
      type: "warning",
      paddingInline: "400",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `subscriptions.ResumeSubscriptionModal.description.unpaidWarning.${collectionProcess}_jsx`,
        options: {
          status: statusAfterUnpause,
          external: true,
          href: URLS.KBUpdatePaymentMethod
        },
        elements: {
          Link
        }
      })
    });
  }
  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `subscriptions.ResumeSubscriptionModal.description.${collectionProcess}_jsx`,
    options: {
      status: statusAfterUnpause
    }
  });
}
export function ResumeSubscriptionModal({
  actions,
  objectId
}) {
  var _buyer$email;
  const theme = useTheme();
  const {
    close
  } = actions;
  const {
    trackInteraction
  } = useTracker();
  const {
    lastPaymentDate,
    buyer,
    checkoutEmail,
    collectionProcess
  } = useSubscription();
  const customerEmail = (_buyer$email = buyer === null || buyer === void 0 ? void 0 : buyer.email) !== null && _buyer$email !== void 0 ? _buyer$email : checkoutEmail;
  const {
    data: queryData,
    loading: queryLoading
  } = useResumeSubscriptionQuery(objectId);
  const [proposedBillingDate, setProposedBillingDate] = useState(0);
  const [onSubmit, {
    loading: submitLoading
  }] = useResumeSubscription({
    objectId,
    proposedNextBillingDate: proposedBillingDate,
    onClose: () => {
      close();
      actions.revalidate().catch(handleError);
    }
  });
  const {
    sendEmailForSubscriptionPaused
  } = useNotificationsQuery();
  const getNotificationMessage = () => {
    if (sendEmailForSubscriptionPaused) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "subscriptions.ResumeSubscriptionModal.description_jsx",
        options: {
          href: URLS.KBEmailConfirmation,
          email: customerEmail,
          external: true
        },
        elements: {
          Link
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "subscriptions.ResumeSubscriptionModal.descriptionNoNotify"
    });
  };
  if (queryLoading || !queryData) {
    return /*#__PURE__*/_jsx(Modal, {
      "data-test-id": TEST_IDS.Resume,
      children: /*#__PURE__*/_jsxs(Form, {
        children: [/*#__PURE__*/_jsxs(DialogHeader, {
          children: [/*#__PURE__*/_jsx(H2, {
            paddingBlock: "400",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "subscriptions.ResumeSubscriptionModal.title"
            })
          }), /*#__PURE__*/_jsx(DialogCloseButton, {
            onClick: close
          })]
        }), /*#__PURE__*/_jsx(DialogBody, {
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(AbstractProgress, {
              Component: NanoProgress,
              maximum: 35
            }), /*#__PURE__*/_jsx(LoadingSpinner, {
              size: "small",
              minHeight: 100,
              layout: "centered",
              grow: true,
              showLabel: true,
              "data-test-id": `${TEST_IDS.Resume}:loading`,
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.ResumeSubscriptionModal.loadingMessage"
              })
            })]
          })
        })]
      })
    });
  }
  const {
    earliestBillingDate,
    proposedNextBillingDate,
    statusAfterUnpause
  } = queryData;
  if (proposedNextBillingDate && proposedBillingDate === 0) {
    setProposedBillingDate(proposedNextBillingDate);
  }
  return /*#__PURE__*/_jsx(Modal, {
    "data-test-id": TEST_IDS.Resume,
    children: /*#__PURE__*/_jsxs(Form, {
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          paddingBlock: "400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.title"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: () => {
            trackInteraction('resumeSubscriptionModalClosed');
            close();
          }
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        children: [getDescriptionMessage(collectionProcess, statusAfterUnpause), /*#__PURE__*/_jsx(HR, {
          marginBlockStart: "400",
          marginBlockEnd: "400"
        }), /*#__PURE__*/_jsx(StyledTile, {
          theme: theme,
          children: /*#__PURE__*/_jsxs(Grid, {
            marginBlock: "200",
            children: [/*#__PURE__*/_jsxs(GridItem, {
              size: 6,
              children: [/*#__PURE__*/_jsx(Text, {
                as: "small",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: 'subscriptions.ResumeSubscriptionModal.lastBillingDate'
                })
              }), /*#__PURE__*/_jsx(Text, {
                as: "p",
                marginBlockEnd: "0",
                children: !lastPaymentDate ? /*#__PURE__*/_jsx("span", {
                  children: EMPTY_VALUE
                }) : /*#__PURE__*/_jsx(FormattedDateTime, {
                  type: "utc",
                  value: Number(lastPaymentDate),
                  format: "ll"
                })
              })]
            }), /*#__PURE__*/_jsxs(GridItem, {
              size: 6,
              children: [/*#__PURE__*/_jsx(Text, {
                as: "small",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "subscriptions.ResumeSubscriptionModal.nextBillingDate"
                })
              }), /*#__PURE__*/_jsx(Text, {
                as: "p",
                marginBlockEnd: "0",
                children: /*#__PURE__*/_jsx(FormattedDateTime, {
                  type: "utc",
                  value: Number(proposedNextBillingDate),
                  format: "ll"
                })
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.datePicker.title"
          }),
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.datePicker.tooltip"
          }),
          children: /*#__PURE__*/_jsx(DateInput, {
            "data-test-id": `${TEST_IDS.Resume}:date-picker`,
            min: createSimpleDate(earliestBillingDate),
            minValidationMessage: I18n.text('subscriptions.ResumeSubscriptionModal.datePicker.minValidationMessage'),
            onChange: ({
              currentTarget: {
                value
              }
            }) => {
              let proposedDate;
              if (value !== null) {
                proposedDate = makeMoment('utc', value).valueOf();
              } else {
                proposedDate = makeMoment('utc', EmptySimpleDate).valueOf();
              }
              if (!proposedDate) {
                setProposedBillingDate(proposedNextBillingDate);
              } else {
                setProposedBillingDate(proposedDate);
              }
            }
          })
        }), /*#__PURE__*/_jsx(HR, {
          marginBlockStart: "400",
          marginBlockEnd: "400"
        }), getNotificationMessage()]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(LoadingButton, {
          "data-test-id": `${TEST_IDS.Resume}:submit`,
          use: "primary",
          loading: submitLoading,
          onClick: () => {
            onSubmit().catch(e => handleError(e));
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.resumeButton"
          })
        }), /*#__PURE__*/_jsx(Button, {
          "data-test-id": `${TEST_IDS.Resume}:cancel`,
          use: "secondary",
          onClick: () => {
            trackInteraction('resumeSubscriptionModalClosed');
            close();
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.ResumeSubscriptionModal.cancelButton"
          })
        })]
      })]
    })
  });
}